import type {CourseReview} from '@/features/instructor-profile/lib/profile-api';
import type {CourseOption} from '@/features/instructor-profile/lib/types';

export const MAX_REVIEWS = 12;

/**
 * Reviews requested per course. The API returns them ordered by
 * `course_review_score__rank`, whose top slots are deliberately sampled across
 * the course's rating distribution — so a small fetch reliably surfaces
 * low-rated reviews. Over-fetching gives us a candidate pool to re-rank by
 * rating before capping at MAX_REVIEWS.
 */
export const REVIEW_CANDIDATES_PER_COURSE = 15;

/** Courses the reviews section draws from when the profile has no featured courses. */
export const FALLBACK_REVIEW_COURSE_COUNT = 2;

/** A review annotated with its position in its own course's ranked batch. */
interface RankedReview extends CourseReview {
    rank: number;
}

export interface RankedReviewsResult {
    reviews: CourseReview[];
    /** At least one course's reviews failed to load (the others still render). */
    hadError: boolean;
}

/**
 * Merges the per-course review batches into the final list shown on the
 * profile. Shared by the client component and the server prefetch so both rank
 * identically.
 *
 * Each review is annotated with its course title and its position within that
 * course's batch (the API's `course_review_score__rank` ordering, so the index
 * is the rank). Highest rated first; ties fall back to the backend's quality
 * rank so course order never decides which of two 5.0s wins.
 */
export function mergeAndRankReviews(
    courses: Pick<CourseOption, 'id' | 'title'>[],
    settled: PromiseSettledResult<{results: CourseReview[]}>[],
): RankedReviewsResult {
    const merged: RankedReview[] = [];
    let hadError = false;
    settled.forEach((outcome, i) => {
        if (outcome.status === 'rejected') {
            hadError = true;
            return;
        }
        outcome.value.results.forEach((r, rank) => {
            merged.push({...r, courseTitle: courses[i].title, rank});
        });
    });

    merged.sort((a, b) => b.rating - a.rating || a.rank - b.rank);

    return {reviews: merged.slice(0, MAX_REVIEWS), hadError};
}
